import { useMemo, useState } from 'react';
import { Icon } from '../common';
import { useWindowedBookings } from '../bookings/useWindowedBookings';
import { dayStatWindows, dogCount, sameWindow } from '../bookings/domain';
import { useShifts } from '../staff/useShifts';
import { useStaff } from '../staff/useStaff';
import { onDutyTeams, arrivingOn, arrivingDogs, residentDogs, residenceCount } from '../today/today';
import { dogsLeavingOn, buildLeavingMessage } from './buildLeavingMessage';
import { MessagesStatStrip } from './MessagesStatStrip';
import { MessagesSkeleton } from './MessagesSkeleton';

// The Messages workspace: a topic rail (left) and the selected topic's message
// (right). Only "Dogs Leaving" is live today; the rest are parked, visible so
// the roadmap reads but not selectable. The selected day is owned by the page
// header (the shared centred date control) and passed in as `anchor`.

interface Topic {
  key: string;
  label: string;
  category: string;
  live: boolean;
}

// Rail contents — order and grouping are the validated prototype's visual
// contract. New topics flip `live: true` once built.
const TOPICS: Topic[] = [
  { key: 'dogs-leaving', label: 'Dogs Leaving', category: 'For staff', live: true },
  { key: 'dogs-arriving', label: 'Dogs arriving today', category: 'For staff', live: false },
  { key: 'headcount', label: "Today's headcount", category: 'For staff', live: false },
  { key: 'booking-confirmed', label: 'Booking confirmed', category: 'For customers', live: false },
  { key: 'vaccines-due', label: 'Vaccines due reminder', category: 'For customers', live: false },
];

const CATEGORIES = ['For staff', 'For customers'];

export function MessagesView({ anchor }: { anchor: string }) {
  // Only Dogs Leaving is selectable, so it is the fixed active topic for now;
  // the rail still renders the parked ones (disabled) for the roadmap.
  const [activeKey] = useState('dogs-leaving');
  const [copied, setCopied] = useState(false);

  // ±2-day window + adjacent prefetch (shared with Daily) — stepping one day
  // stays INSIDE the held set, so the strip + message never blank or jump
  // mid-fetch and the step resolves from cache. One windowed read, no new server.
  const { range: statRange, prefetch: prefetchRanges } = useMemo(() => dayStatWindows(anchor), [anchor]);
  const { bookings, loading: bookingsLoading, isPending, window: shownWindow } = useWindowedBookings(
    statRange,
    prefetchRanges,
  );
  const { shifts, loading: shiftsLoading } = useShifts(statRange.from, statRange.to);
  const staffState = useStaff();

  // The shown bookings are authoritative for `anchor` only when their window
  // actually covers it. On cold load (nothing held) or a far jump (held window
  // doesn't yet reach the new day) we must NOT build a message off stale/empty
  // data — show the skeleton until the day's own data lands, so Copy can never
  // capture "No dogs leaving today" for a day that has departures (Codex P2).
  // `shownWindow` is null until a first response lands, which is precisely
  // NOT ready — an empty placeholder is not a window containing the anchor.
  const dataReady = shownWindow != null && anchor >= shownWindow.from && anchor <= shownWindow.to;
  if (bookingsLoading || shiftsLoading || staffState.state.phase === 'loading' || !dataReady) {
    return <MessagesSkeleton />;
  }

  const staff = staffState.state.phase === 'ready' ? staffState.state.staff : [];
  const leaving = dogsLeavingOn(bookings, anchor);
  const message = buildLeavingMessage(anchor, leaving);
  const duty = onDutyTeams(shifts, staff, anchor);
  const arriving = arrivingDogs(bookings, anchor);
  const arrivingCount = dogCount(arrivingOn(bookings, anchor));
  const residents = residentDogs(bookings, anchor);
  const inResidence = residenceCount(bookings, anchor);

  // Dim only while a *different* day's window is loading (a real step) — a
  // same-window revalidation (keep-alive activation) must stay invisible.
  const stepping =
    isPending && !sameWindow(shownWindow, statRange);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      // Hold the confirmed state long enough to read and feel, then settle back.
      window.setTimeout(() => setCopied(false), 2200);
    } catch {
      // Clipboard denied (rare, permissioned contexts) — leave the button as
      // is; the message text is on screen to copy by hand.
    }
  };

  return (
    <div className={'messages' + (stepping ? ' is-stepping' : '')}>
      <MessagesStatStrip
        anchor={anchor}
        duty={duty}
        arriving={arriving}
        arrivingCount={arrivingCount}
        residents={residents}
        inResidence={inResidence}
      />
      <div className="messages-view">
      <aside className="msg-list">
        {CATEGORIES.map((cat) => (
          <div className="msg-cat-group" key={cat}>
            <div className="msg-cat-label">{cat}</div>
            {TOPICS.filter((t) => t.category === cat).map((t) =>
              t.live ? (
                <button
                  key={t.key}
                  className={'msg-list-item' + (t.key === activeKey ? ' active' : '')}
                >
                  {t.label}
                </button>
              ) : (
                <button key={t.key} className="msg-list-item soon" disabled>
                  {t.label}
                  <span className="soon-tag">soon</span>
                </button>
              ),
            )}
          </div>
        ))}
      </aside>

      <section className="msg-pane">
        <div className="msg-pane-head">
          <div className="msg-title-group">
            <h2>Dogs Leaving</h2>
            <span className={'dogs-in' + (leaving.length === 0 ? ' zero' : '')}>{leaving.length}</span>
          </div>
          <button
            className={'btn primary copy-btn' + (copied ? ' is-copied' : '')}
            onClick={copy}
            aria-label={copied ? 'Message copied to clipboard' : 'Copy message to clipboard'}
          >
            <span className="copy-ico" aria-hidden="true">
              <Icon name={copied ? 'check' : 'copy'} size={15} />
            </span>
            <span className="copy-label">{copied ? 'Copied' : 'Copy Message'}</span>
          </button>
        </div>

        {/* Screen-reader confirmation — the visual tick + colour shift is the
            sighted cue; this announces the same the moment the copy lands. */}
        <span className="sr-only" role="status" aria-live="polite">
          {copied ? 'Message copied to clipboard' : ''}
        </span>

        <pre className="msg-body">{message}</pre>
        <p className="msg-hint">
          {leaving.length === 0 ? (
            'Nothing to send today.'
          ) : (
            <>
              Click Copy Message, then go to{' '}
              <a href="https://web.whatsapp.com" target="_blank" rel="noopener noreferrer">
                WhatsApp
              </a>
              , search for the helper and hit CMD + V to paste the message.
            </>
          )}
        </p>
      </section>
      </div>
    </div>
  );
}
